import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  signIn,
  signUp,
  confirmSignIn,
  updatePassword,
  getCurrentUser,
  resetPassword,
  confirmResetPassword,
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference,
} from "aws-amplify/auth";
import type { SignInInput } from "aws-amplify/auth";
import api, { logoutAndSignOut } from "../lib/apiClient";
import { reportReauthCompletionIfLocked } from "../lib/native/sessionLockTelemetry";
import { reauthCredential } from "../lib/native/reauthCredential";
import { getOrCreateDeviceId } from "../lib/native/deviceId";
import {
  clearLocalSession,
  isReauthClientConfigured,
  selectCognitoClient,
} from "../lib/cognitoClients";
import { DeadlineExceededError, withDeadline } from "../lib/withDeadline";
import {
  addReauthProfile,
  clearAllReauthProfiles,
  listReauthProfiles,
  removeReauthProfile,
} from "../lib/native/reauthProfileCache";
import { captureException } from "../lib/sentry";
import { useAssumedRole } from "../contexts/AssumedRoleContext";
import { can } from "../lib/access";
import type {
  AppRole,
  AuthContext,
  ReauthDenialCode,
  LoginCredentials,
  SignupData,
} from "@livingsyncs/types";

// ─── Auth context ─────────────────────────────────────────────────────────────

export function useAuth() {
  return useQuery<AuthContext>({
    queryKey: ["auth-context"],
    queryFn: async () => {
      await getCurrentUser(); // throws if not authenticated
      const { data } = await api.get("/auth/me");
      return data.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
    retryOnMount: false,
    // A logged-out visitor (e.g. the login page) makes this query throw an
    // Amplify "not authenticated" error with no HTTP response — the global
    // QueryCache net would otherwise misread that as "check your connection".
    // Auth-state failure is expected here and is owned by the routing/login UI.
    meta: { suppressErrorNotification: true },
  });
}

// ─── Permission helpers ───────────────────────────────────────────────────────
//
// All permission checks below delegate to the shared, implication-aware `can()`
// predicate (@livingsyncs/permissions, via @/lib/access) — the same rules the
// backend enforces with (US-044 AC2). The local PERMISSION_IMPLIES copy +
// private checkPermission are gone.
//
// Per-hook parity note (US-044): the swap is exact because `can()` reduces to a
// plain `.includes()` for any key that is NOT an implied child. The only implied
// children are staff:view, calendar:view, cases:view, schedules:view,
// incidents:report, residents:view — and the only hooks checking an implied
// child (useCanViewCalendar→calendar:view, useCanViewStaff→staff:view) already
// used the implication-aware checkPermission, so their behaviour is unchanged.
// useCanCreateCareNotes→care:document also previously used checkPermission, but
// care:document is standalone (not an implied child), so it reduced to
// `.includes()` then and now — identical. Hooks checking parent/standalone keys
// (useIsManager→staff:manage, useIsAdmin→org:group_home_manage,
// useCanManageAppointments→calendar:manage, useCanManageMedicalRecords→
// residents:medical) keep identical behaviour. Guarded by useAuth.canX.test.ts.

// ─── Role helpers ─────────────────────────────────────────────────────────────

// Accepts `AppRole` so a typo like `useHasRole("medical-staff")` (dash vs
// underscore) becomes a compile error instead of silently returning false.
export function useHasRole(roleName: AppRole): boolean {
  const { data: context } = useAuth();
  return context?.roles.includes(roleName) ?? false;
}

export function useCanAccessFacility(facilityId: string): boolean {
  const { data: context } = useAuth();
  if (!context) return false;
  return (
    context.isPlatformAdmin ||
    context.isPlatformManager ||
    context.facilityIds.includes(facilityId)
  );
}

/**
 * Platform-tier user (platform admin OR platform manager) — the one shared
 * predicate for platform-only UI affordances. Replaces the ad-hoc
 * `isPlatformAdmin || isPlatformManager` compositions that were copy-pasted
 * per page (UI tier only — the backend enforces actual access).
 */
export function useIsPlatformUser(): boolean {
  const { data: context } = useAuth();
  if (!context) return false;
  return context.isPlatformAdmin || context.isPlatformManager;
}

export function useIsManager(): boolean {
  const { data: context } = useAuth();
  if (!context) return false;
  // Platform super-users also see management affordances (UI tier only — the
  // backend enforces actual access; this is not a PHI gate).
  return (
    context.isPlatformAdmin ||
    context.isPlatformManager ||
    context.roles.some(
      (r) => r === "role:org:admin" || r === "role:facility:manager",
    )
  );
}

export function useIsAdmin(): boolean {
  const { data: context } = useAuth();
  if (!context) return false;
  return (
    context.isGroupHomeAdmin ||
    context.isPlatformAdmin ||
    context.isPlatformManager
  );
}

export function useCanManageAppointments(): boolean {
  const { data: context } = useAuth();
  return can({ permissions: context?.permissions ?? [] }, "appointment:write");
}

export function useCanViewCalendar(): boolean {
  const { data: context } = useAuth();
  return can(
    { permissions: context?.permissions ?? [] },
    "calendar-event:read",
  );
}

export function useCanViewStaff(): boolean {
  const { data: context } = useAuth();
  return can({ permissions: context?.permissions ?? [] }, "employee:read");
}

export function useCanManageMedicalRecords(): boolean {
  const { data: context } = useAuth();
  return can({ permissions: context?.permissions ?? [] }, "care-plan:write");
}

export function useCanCreateCareNotes(): boolean {
  const { data: context } = useAuth();
  return can({ permissions: context?.permissions ?? [] }, "care-note:write");
}

// ─── Auth step machine ────────────────────────────────────────────────────────
//
// Cognito's hosted sign-in is a multi-step challenge protocol. Amplify v6
// surfaces each challenge via `nextStep.signInStep`; we map those to a UI
// state and render the matching form on the LoginPage.
//
//   idle                   → email + password form
//   new_password_required  → admin-invited user must replace their temp
//                            password (Cognito "FORCE_CHANGE_PASSWORD")
//   mfa_setup              → first-time TOTP enrollment (pool has MFA on)
//   mfa_required           → returning user enters TOTP code
//
// New-password and MFA-setup both come from the same sign-in attempt — a
// user invited into an MFA-on pool can pass through new_password_required
// → mfa_setup → done in a single session. Both `confirmSignIn(...)` calls
// (new password, TOTP code) go through the same `handleNextStep` router
// below so the chain works regardless of order.

export type AuthStep =
  "idle" | "new_password_required" | "mfa_required" | "mfa_setup";

// Loosely typed because Amplify's discriminated union is finicky across
// versions; we only branch on `signInStep` here. The TOTP setup details
// (when present) live at `nextStep.totpSetupDetails`.
type AmplifyNextStep = {
  signInStep: string;
  totpSetupDetails?: {
    getSetupUri: (appName: string, accountName?: string) => URL;
  };
  // US-176 Phase 2 — CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE carries
  // CreateAuthChallenge's publicChallengeParameters here. Only `nonce` is
  // ever read from it.
  additionalInfo?: Record<string, string>;
};

// US-176 Phase 2 — result of attempting to mint a device credential after a
// full sign-in. Never throws — every failure mode is a variant here instead,
// because the caller (LoginPage) needs to know WHETHER to show the forced
// "set your PIN" step, not just whether something went wrong.
//
//   "unavailable" — this device structurally cannot hold a credential (no
//                   Secure Enclave/safeStorage, or web). No PIN step is
//                   useful here; sign-in proceeds exactly as before.
//   "minted"      — credential registered. `hasReauthPin` says whether the
//                   forced PIN-setup step is still needed.
//   "denied"      — the register call came back 403. `reason` comes from the
//                   response's ReauthDenialCode, so the UI can say what the
//                   user should actually do (enable 2FA vs check in first).
//   "failed"      — anything else (network, unexpected response shape, or
//                   the whole attempt timing out — see MINT_TIMEOUT_MS).
export type MintResult =
  | { status: "unavailable" }
  | { status: "minted"; hasReauthPin: boolean }
  | { status: "denied"; reason: ReauthDenialReason }
  | { status: "failed" };

// Client-side mirror of the backend's ReauthDenialCode, plus "unknown" for a
// 403 that carries no code (older backend, CSRF retry exhausted, or any
// other permission denial on these routes). "unknown" deliberately does NOT
// fall back to "mfa_required" — telling someone to enable 2FA when that
// isn't the problem is exactly the bug this replaced.
export type ReauthDenialReason =
  | "mfa_required"
  | "no_employee_record"
  | "no_open_shift"
  | "temporarily_unavailable"
  | "unknown";

const DENIAL_CODE_TO_REASON: Record<ReauthDenialCode, ReauthDenialReason> = {
  MFA_PROVENANCE_REQUIRED: "mfa_required",
  NO_EMPLOYEE_RECORD: "no_employee_record",
  NO_OPEN_SHIFT: "no_open_shift",
  FAST_REAUTH_UNAVAILABLE: "temporarily_unavailable",
};

/** 403 → a denial reason; anything else → null (not a denial). */
function denialReasonFrom(err: unknown): ReauthDenialReason | null {
  const response = (
    err as {
      response?: {
        status?: number;
        data?: { code?: unknown; error?: unknown };
      };
    }
  )?.response;
  if (response?.status !== 403) return null;
  const code = response.data?.code;
  // A CSRF rejection comes from middleware.ts before the route runs: it says
  // nothing about the account, and a retry can succeed. Report it as a
  // failed attempt ("try again"), not as "couldn't be set up for this
  // account" — seen live on the iPad 2026-09-19.
  const error = response.data?.error;
  if (
    code === undefined &&
    typeof error === "string" &&
    error.includes("CSRF")
  ) {
    return null;
  }
  // Own-property check, not `in`: `in` also matches Object.prototype keys, so
  // a code of "constructor" would return a function instead of a reason.
  return typeof code === "string" &&
    Object.prototype.hasOwnProperty.call(DENIAL_CODE_TO_REASON, code)
    ? DENIAL_CODE_TO_REASON[code as ReauthDenialCode]
    : "unknown";
}

/** User-facing guidance for a refused PIN/credential setup. */
export function describeReauthDenial(reason: ReauthDenialReason): string {
  switch (reason) {
    case "mfa_required":
      return "This account needs two-factor authentication before a PIN can be set up. Turn it on from your account menu → Profile → Two-Factor Authentication, then sign out and back in.";
    case "no_open_shift":
      return "Fast sign-in is tied to your current check-in. Check in for your shift, then sign out and back in to set it up.";
    case "no_employee_record":
      return "Fast sign-in is only available for staff accounts.";
    case "temporarily_unavailable":
      return "Fast sign-in can't be set up right now. It will be offered again the next time you sign in.";
    case "unknown":
      return "Fast sign-in couldn't be set up for this account.";
  }
}

/**
 * US-176 — what to tell a user whose PIN sign-in failed.
 *
 * Cognito reports a failed CUSTOM_AUTH challenge and a CRASHED challenge
 * Lambda through the same sign-in call, and the screen used to label both
 * "Incorrect PIN" and print the raw message. On 2026-09-19 a correct PIN
 * read as "Incorrect PIN — VerifyAuthChallengeResponse failed with error
 * ENOENT ... '/app/rds-ca.pem'": the user was blamed for an infrastructure
 * fault, and an internal path was shown to them.
 *
 * Only NotAuthorizedException means "the factors didn't verify" (wrong PIN,
 * wrong device, or the server-side lockout). Everything else — the Lambda
 * exception family, our own unexpected-step guards, network failures — is
 * ours, not theirs. Raw messages are never surfaced either way.
 */
export function describePinLoginError(error: unknown): {
  title: string;
  message: string;
} {
  const name = (error as { name?: string } | null)?.name;
  if (error instanceof DeadlineExceededError) {
    return {
      title: "Sign-in is taking too long",
      message:
        "This isn't your PIN — we couldn't reach the server. Check the connection and try again, or sign in with your password.",
    };
  }
  if (name === "NotAuthorizedException" || name === "UserNotFoundException") {
    // Cognito reports a wrong PIN and an EXPIRED device registration the same
    // way (generic challenge failure), and the client cannot tell them apart —
    // so say both rather than blame the PIN outright. Live on the iPad
    // 2026-09-20: a correct PIN read "Incorrect PIN" because the device
    // registration had lapsed with the previous check-in.
    return {
      title: "PIN sign-in didn't work",
      message:
        "Check the PIN and try again. If it still fails, fast sign-in may have expired with your last check-in — sign in with your password once and it will work again. After several failed attempts, PIN sign-in locks for 15 minutes.",
    };
  }
  return {
    title: "Fast sign-in isn't working right now",
    message:
      "This isn't your PIN — something went wrong on our side. Sign in with your password instead.",
  };
}

// Review fix (cycle 1, Critical) — without a bound, a stalled connection
// (TCP connects but nothing ever responds — not a clean rejection, so the
// catch block below never fires) left the caller's "checking" screen stuck
// forever, with no timeout and no visible way through. That directly
// contradicts spec C-6 ("a PIN lockout must never block care"), which this
// whole fix exists to extend to PIN SETUP too. 10s is generous for a
// same-region API call but short enough that a real stall resolves to the
// existing "failed" UX path (which already has a Continue button) well
// within a caregiver's patience.
//
// The underlying work is NOT cancelled on timeout — if it succeeds moments
// later, addReauthProfile() still runs from the original (now-orphaned)
// promise chain, caching the profile for next time. That's a safe, silent
// background effect (a localStorage write), not a correctness problem: the
// caller has already moved on by then, exactly as intended.
const MINT_TIMEOUT_MS = 10_000;

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
): Promise<T | "timeout"> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve("timeout"), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        // Review fix (cycle 2) — this branch previously only cleared the
        // timer, so a rejection would leave the wrapper settled by NEITHER
        // path and hang forever: the exact bug this wrapper exists to close.
        // attemptMint never rejects today, but that invariant is unenforced;
        // settle as "timeout" (-> "failed" upstream) rather than rely on it.
        clearTimeout(timer);
        resolve("timeout");
      },
    );
  });
}

async function attemptMint(email: string): Promise<MintResult> {
  try {
    const publicKeyPem = await reauthCredential.mint();
    await api.post("/me/reauth-credential", {
      device_id: getOrCreateDeviceId(),
      public_key: publicKeyPem,
    });
    let displayName = email;
    let hasReauthPin = false;
    try {
      const { data } = await api.get("/auth/me");
      const meContext = data.data as AuthContext;
      displayName = meContext.name || email;
      hasReauthPin = meContext.hasReauthPin ?? false;
    } catch {
      // Non-fatal — the picker just shows the email instead of a name, and
      // we fall through to treating the PIN as unset (the safer of the two
      // guesses: worst case the user re-enters a PIN they already had).
    }
    addReauthProfile({ email, displayName });
    return { status: "minted", hasReauthPin };
  } catch (err) {
    const reason = denialReasonFrom(err);
    captureException(err instanceof Error ? err : new Error(String(err)), {
      operation: "mintReauthCredentialIfPossible",
      source: "useAuth",
    });
    return reason ? { status: "denied", reason } : { status: "failed" };
  }
}

// Exported (matches desktop/updater.ts's classifyUpdateError precedent) so
// the classification logic — which decides what a user sees after a
// sign-in, including the security-relevant "denied" case — is directly
// unit-testable without standing up useLogin()'s full Amplify/mutation
// scaffolding.
export async function mintReauthCredentialIfPossible(
  email: string,
): Promise<MintResult> {
  if (!reauthCredential.available) return { status: "unavailable" };
  const outcome = await withTimeout(attemptMint(email), MINT_TIMEOUT_MS);
  if (outcome === "timeout") {
    captureException(new Error("mintReauthCredentialIfPossible timed out"), {
      operation: "mintReauthCredentialIfPossible",
      source: "useAuth",
    });
    return { status: "failed" };
  }
  return outcome;
}

// ─── Login ────────────────────────────────────────────────────────────────────

/**
 * Sign in from a clean local slate — both the password and the PIN path.
 *
 * A stale session can still be in storage when /login is reached: an
 * inactivity auto-logout or an iPad lock whose sign-out raced the redirect
 * (SCRUM-140; NativeSessionGuard / logoutAndSignOut). Amplify then throws
 * `UserAlreadyAuthenticatedException` from signIn().
 *
 * The leftovers are cleared LOCALLY, never via signOut(). Both alternatives
 * hang on the iPad after a lock/wake (read in @aws-amplify/auth 6.20.0):
 * signIn()'s own "already signed in?" pre-check calls getCurrentUser(), which
 * REFRESHES expired tokens over the network, and signOut() calls Cognito's
 * revoke endpoint before clearing anything. Neither has a timeout. The caller
 * has already run selectCognitoClient(), so this clears that client's keys —
 * selectCognitoClient() itself only clears on a client SWITCH.
 *
 * The clear is repeated before the single retry: an in-flight token refresh
 * started elsewhere (LoginPage's own session check) can write tokens back
 * after the first clear.
 */
async function signInFresh(input: SignInInput) {
  await clearLocalSession();
  try {
    return await signIn(input);
  } catch (err) {
    if (
      (err as { name?: string })?.name !== "UserAlreadyAuthenticatedException"
    ) {
      throw err;
    }
    await clearLocalSession();
    return await signIn(input);
  }
}

/** A newer-attempt race left Amplify's sign-in state pointing at another
 * attempt's challenge; this one can't be completed. */
class SignInInterruptedError extends Error {
  constructor() {
    super("Sign-in was interrupted — please sign in again.");
    this.name = "SignInInterruptedError";
  }
}

// Upper bound on a password sign-in's first step (SRP is two round trips).
// Without it a stalled connection left the "Sign in" button spinning forever.
const SIGN_IN_TIMEOUT_MS = 20_000;

// Upper bound on a whole PIN sign-in (clear → signIn → device signature →
// confirmSignIn). The three Cognito Lambdas it drives finish in under 2.5s
// even cold (preprod CloudWatch, 2026-09-22), so 20s only ever trips on a
// genuinely stalled connection — and then the PIN boxes come back with an
// error instead of staying disabled forever.
const PIN_SIGN_IN_TIMEOUT_MS = 20_000;

// US-176 Phase 2 — gates LoginPage's navigate-away-on-success until we know
// whether this device still needs the forced "set your PIN" step.
//   checking — mint is in flight; block navigation, show nothing alarming.
//   setup    — mint succeeded, no PIN on file yet; render the PIN form.
//   blocked  — mint could not complete (see MintResult's "denied"/"failed");
//              tell the user plainly and let them continue anyway (spec
//              C-6: this feature must never block getting into the app).
type PinSetupState =
  | { phase: "checking" }
  | { phase: "setup"; email: string }
  | {
      phase: "blocked";
      email: string;
      reason: "failed" | ReauthDenialReason;
    };

export function useLogin() {
  const queryClient = useQueryClient();
  const [authStep, setAuthStep] = useState<AuthStep>("idle");
  const [totpSetupUri, setTotpSetupUri] = useState<string | null>(null);
  // Cached so the TOTP setup URI we generate in step 2 (mfa_setup) carries
  // the right account-label after new-password confirmation. signIn knows
  // the email; confirmSignIn does not.
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [pinSetupState, setPinSetupState] = useState<PinSetupState | null>(
    null,
  );
  // Which mint attempt is current. Every mintIfDone call takes the next id,
  // and "Skip for now" also advances it, so a mint callback only applies its
  // result if nothing has superseded it since it started. This covers both
  // late-result cases: a user who skipped during "checking" (the callback
  // must not reopen the screen under them), AND a second full sign-in that
  // starts before the first attempt's callback lands (the stale result must
  // not overwrite the live one). The previous boolean skip-latch covered only
  // the first — it was reset at the start of attempt #2, so attempt #1's late
  // callback read it as "not skipped" and clobbered #2.
  const mintAttemptRef = useRef(0);
  // Amplify keeps ONE module-global sign-in state (signInStore): signIn()
  // writes it when Cognito answers (resets it on failure), and
  // confirmSignIn() reads it synchronously as its first statement. A sign-in
  // abandoned at its deadline is NOT cancelled, so if its request answers
  // late it overwrites the state a newer attempt is about to confirm
  // against. These three refs make that detectable:
  //   signInAttemptRef — every password or PIN sign-in takes the next id; a
  //     PIN timeout also advances it, so the abandoned attempt stops itself.
  //   signInStateOwnerRef — the id of the attempt whose signIn() last
  //     settled, i.e. who last wrote Amplify's state (orphans included).
  //   passwordAttemptRef — the attempt whose challenge the password screens
  //     (new password / MFA) are currently showing.
  // The owner is checked immediately before confirmSignIn(), in the same
  // synchronous tick, so nothing can write the state between the check and
  // Amplify's read. The owner ref itself lags Amplify's write by a few
  // microtasks (it is set once signIn()'s promise unwinds), but the two
  // continuations that could race come from different event sources — a
  // network response vs. the native signing bridge — so they don't share a
  // microtask drain. Effectively exact, not provably so; if the check ever
  // misses, the retry just fails and can be retried.
  const signInAttemptRef = useRef(0);
  const signInStateOwnerRef = useRef(0);
  const passwordAttemptRef = useRef(0);

  const ownedSignIn = async (attempt: number, input: SignInInput) => {
    try {
      return await signInFresh(input);
    } finally {
      signInStateOwnerRef.current = attempt;
    }
  };

  // The password screens' confirmSignIn() would otherwise send the user's
  // code or new password against a session a late, abandoned attempt wrote.
  const assertPasswordChallengeIntact = () => {
    if (signInStateOwnerRef.current !== passwordAttemptRef.current) {
      throw new SignInInterruptedError();
    }
  };

  // Route the next-step Cognito returned to the matching UI state. Called
  // after signIn() and after each confirmSignIn() — any of them can land on
  // any subsequent challenge in the chain.
  const handleNextStep = (nextStep: AmplifyNextStep, email: string | null) => {
    switch (nextStep.signInStep) {
      case "DONE":
        setAuthStep("idle");
        setTotpSetupUri(null);
        setPendingEmail(null);
        queryClient.invalidateQueries({ queryKey: ["auth-context"] });
        // US-176 Phase 0 — the ONE place every completion path converges
        // (password-only, +new-password, +MFA all land here), so this is
        // the single correct spot to report a resume-from-lock duration.
        // No-ops silently when no lock marker exists (web/Electron, or a
        // normal cold sign-in on iPad), or when the marker belongs to a
        // DIFFERENT employee (review fix, Critical — see
        // sessionLockTelemetry.ts). `email` is this handler's own
        // parameter, always the account that just completed sign-in.
        reportReauthCompletionIfLocked(email);
        break;
      case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
        setAuthStep("new_password_required");
        break;
      case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        setAuthStep("mfa_required");
        break;
      case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP": {
        // Amplify's type narrowing for the setup details is fiddly; we already
        // checked the discriminant above, so the helper is safe to read.
        if (nextStep.totpSetupDetails && email) {
          const uri = nextStep.totpSetupDetails.getSetupUri(
            "LivingSyncs",
            email,
          );
          setTotpSetupUri(uri.toString());
        }
        setAuthStep("mfa_setup");
        break;
      }
      // Any other step (DONE_REQUIRED, RESET_PASSWORD, …) we don't support
      // yet — leave the user on the current screen with whatever error
      // mutation surfaced via React Query.
    }
  };

  // US-176 Phase 2 — a device credential is only ever minted after a FULL
  // password(+MFA) sign-in genuinely reaches DONE (spec §4.2: "on success:
  // generate an asymmetric keypair..."). Called from the three mutations
  // below that represent that path; deliberately NOT from
  // loginWithPinMutation, which never mints anything.
  //
  // Blocks LoginPage's navigate-away (via pinSetupState) while the mint is
  // in flight, on every platform uniformly — mintReauthCredentialIfPossible
  // itself resolves to "unavailable" near-instantly on the web (no network
  // call at all, see its own early return), so this never introduces a
  // visible delay there; it only ever actually holds the screen on a native
  // shell where a credential is genuinely possible.
  //
  // Review fix (cycle 1, Minor) — "near-instantly" still means at least one
  // React commit with pinSetupPhase:"checking" (a promise, even one that
  // resolves on the next microtask, still yields before its .then() runs).
  // Checking `reauthCredential.available` HERE, synchronously, before ever
  // setting "checking", skips that commit entirely on a platform that could
  // never use this screen anyway — closing a cosmetic flash the original
  // comment claimed didn't exist.
  const mintIfDone = (nextStep: AmplifyNextStep, email: string | null) => {
    if (nextStep.signInStep !== "DONE" || !email) return;
    // No re-auth client in this build → a PIN could never be used to sign
    // in, so don't enrol one (or show the setup screen) at all.
    if (!reauthCredential.available || !isReauthClientConfigured()) return;
    const attempt = ++mintAttemptRef.current;
    setPinSetupState({ phase: "checking" });
    void mintReauthCredentialIfPossible(email).then((result) => {
      // Superseded — by "Skip for now" or by a newer sign-in. Don't touch
      // the screen. addReauthProfile() (inside the mint call itself) still
      // ran on success, so the next lock/sign-in cycle sees the cached
      // profile regardless; only the stale SCREEN update is dropped.
      if (attempt !== mintAttemptRef.current) return;
      switch (result.status) {
        case "unavailable":
          setPinSetupState(null);
          break;
        case "minted":
          setPinSetupState(
            result.hasReauthPin ? null : { phase: "setup", email },
          );
          break;
        case "denied":
          setPinSetupState({ phase: "blocked", email, reason: result.reason });
          break;
        case "failed":
          setPinSetupState({ phase: "blocked", email, reason: "failed" });
          break;
      }
    });
  };

  // An interrupted challenge can't be finished — back to the sign-in form,
  // where the error explains why.
  const restartIfInterrupted = (err: Error) => {
    if (err instanceof SignInInterruptedError) {
      setAuthStep("idle");
      setTotpSetupUri(null);
    }
  };

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      // US-176 — a password sign-in always runs on the main app client
      // (lib/cognitoClients.ts), even if the last session was a PIN one.
      await selectCognitoClient("main");
      const attempt = ++signInAttemptRef.current;
      try {
        const { nextStep } = await withDeadline(
          ownedSignIn(attempt, {
            username: credentials.email,
            password: credentials.password,
          }),
          SIGN_IN_TIMEOUT_MS,
          "Sign-in",
        );
        passwordAttemptRef.current = attempt;
        return { nextStep, email: credentials.email };
      } catch (err) {
        // The form prints error.message verbatim; "Sign-in timed out after
        // 20000ms" would read as a fault in the password.
        if (err instanceof DeadlineExceededError) {
          captureException(err, { operation: "login", source: "useAuth" });
          throw new Error(
            "Sign-in is taking too long — we couldn't reach the server. Check the connection and try again.",
          );
        }
        throw err;
      }
    },
    onSuccess: ({ nextStep, email }) => {
      setPendingEmail(email);
      handleNextStep(nextStep as AmplifyNextStep, email);
      mintIfDone(nextStep as AmplifyNextStep, email);
    },
  });

  const confirmNewPasswordMutation = useMutation({
    mutationFn: async (newPassword: string) => {
      assertPasswordChallengeIntact();
      const { nextStep } = await confirmSignIn({
        challengeResponse: newPassword,
      });
      return nextStep;
    },
    onError: restartIfInterrupted,
    onSuccess: (nextStep) => {
      handleNextStep(nextStep as AmplifyNextStep, pendingEmail);
      mintIfDone(nextStep as AmplifyNextStep, pendingEmail);
    },
  });

  const confirmMfaMutation = useMutation({
    mutationFn: async (code: string) => {
      assertPasswordChallengeIntact();
      const { nextStep } = await confirmSignIn({ challengeResponse: code });
      return nextStep;
    },
    onError: restartIfInterrupted,
    onSuccess: (nextStep) => {
      handleNextStep(nextStep as AmplifyNextStep, pendingEmail);
      mintIfDone(nextStep as AmplifyNextStep, pendingEmail);
    },
  });

  // US-176 Phase 2 — PIN-based fast re-auth (spec §4.2). A single mutation,
  // not a multi-step chain like the password path: the device signs the
  // challenge automatically (no user interaction between "get nonce" and
  // "sign it"), so there is nothing for an intermediate authStep to render.
  // Never mints a credential — only a FULL sign-in does that.
  const loginWithPinMutation = useMutation({
    mutationFn: async ({ email, pin }: { email: string; pin: string }) => {
      // Supersession — the same idiom as mintAttemptRef, shared with the
      // password path (see signInAttemptRef). An attempt abandoned at its
      // deadline stops before signing a nonce or sending the PIN.
      const attempt = ++signInAttemptRef.current;
      const stopIfAbandoned = () => {
        if (attempt !== signInAttemptRef.current) {
          throw new Error("Superseded PIN sign-in attempt");
        }
      };
      try {
        // Bounded — see PIN_SIGN_IN_TIMEOUT_MS. If a stalled attempt
        // completes after the deadline anyway, Amplify still fires its
        // 'signedIn' Hub event and LoginPage's own listener lets the user in.
        return await withDeadline(
          (async () => {
            // US-176 — PIN sessions live on the dedicated re-auth app client;
            // the backend reads the token's `aud` to tell them from full
            // sign-ins.
            await selectCognitoClient("reauth");
            const deviceId = getOrCreateDeviceId();
            // At most one restart: if an abandoned attempt's late answer
            // overwrote Amplify's state while this one was signing, open a
            // fresh challenge instead of confirming against the stale one
            // (which would fail the real PIN and count toward the lockout).
            for (let round = 0; ; round++) {
              const { nextStep: firstStep } = await ownedSignIn(attempt, {
                username: email,
                options: { authFlowType: "CUSTOM_WITHOUT_SRP" },
              });
              stopIfAbandoned();
              const step = firstStep as AmplifyNextStep;
              if (step.signInStep !== "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
                throw new Error(
                  `Unexpected sign-in step for PIN re-auth: ${step.signInStep}`,
                );
              }
              const nonce = step.additionalInfo?.nonce;
              if (!nonce) {
                throw new Error("No challenge nonce returned for PIN re-auth");
              }
              const signature = await reauthCredential.sign(nonce);
              stopIfAbandoned();
              if (signInStateOwnerRef.current === attempt) {
                const { nextStep } = await confirmSignIn({
                  challengeResponse: JSON.stringify({
                    pin,
                    signature,
                    deviceId,
                  }),
                });
                return { nextStep, email };
              }
              if (round >= 1) throw new SignInInterruptedError();
            }
          })(),
          PIN_SIGN_IN_TIMEOUT_MS,
          "PIN sign-in",
        );
      } catch (err) {
        if (err instanceof DeadlineExceededError) signInAttemptRef.current += 1;
        throw err;
      }
    },
    onError: (err) => {
      // Native builds carry no Sentry DSN today, but web/desktop do — and a
      // stall is exactly the failure nobody would otherwise see.
      if (err instanceof DeadlineExceededError) {
        captureException(err, { operation: "loginWithPin", source: "useAuth" });
      }
    },
    onSuccess: ({ nextStep, email }) => {
      setPendingEmail(email);
      handleNextStep(nextStep as AmplifyNextStep, email);
    },
  });

  // US-176 Phase 2 — the forced first-PIN-set step. Self-service, no
  // current-PIN confirmation (route comment: a PIN is not a recovery
  // mechanism, unlike a password). Never mints anything itself.
  const setPinMutation = useMutation({
    mutationFn: async (pin: string) => {
      try {
        await api.post("/me/pin", { pin });
      } catch (err) {
        // A refusal gets the same specific guidance as a refused mint; the
        // raw axios message ("Request failed with status code 403") never
        // reaches the screen. Anything else (validation 400, network) keeps
        // its original error.
        const reason = denialReasonFrom(err);
        if (reason) throw new Error(describeReauthDenial(reason));
        throw err;
      }
    },
    onSuccess: () => {
      setPinSetupState(null); // done — release the navigation LoginPage held
    },
  });

  // Escape hatch for "checking"/"setup"/"blocked" alike — spec C-6's own
  // principle ("a PIN lockout must never block care") applies just as much
  // to PIN SETUP: a user must always be able to get into the app, whether
  // they decline to set one now, the mint is still in flight, or it
  // couldn't complete. Advancing the attempt id makes any in-flight mint
  // callback stale, so it can't reopen this screen after the user left it.
  const skipPinSetupForNow = () => {
    mintAttemptRef.current += 1;
    setPinSetupState(null);
  };

  return {
    mutate: loginMutation.mutate,
    mutateAsync: loginMutation.mutateAsync,
    isPending: loginMutation.isPending,
    error:
      loginMutation.error ??
      confirmNewPasswordMutation.error ??
      confirmMfaMutation.error,
    authStep,
    totpSetupUri,
    confirmNewPassword: confirmNewPasswordMutation.mutate,
    isNewPasswordPending: confirmNewPasswordMutation.isPending,
    confirmMfa: confirmMfaMutation.mutate,
    isConfirmPending: confirmMfaMutation.isPending,
    loginWithPin: loginWithPinMutation.mutate,
    isPinLoginPending: loginWithPinMutation.isPending,
    pinLoginError: loginWithPinMutation.error,
    pinSetupPhase: pinSetupState?.phase ?? null,
    pinSetupBlockedReason:
      pinSetupState?.phase === "blocked" ? pinSetupState.reason : null,
    setPin: setPinMutation.mutate,
    isSetPinPending: setPinMutation.isPending,
    setPinError: setPinMutation.error,
    skipPinSetupForNow,
  };
}

/** US-176 Phase 2 — the lock-screen's device-local profile picker. Thin
 * pass-through so LoginPage doesn't need to know the cache lives in
 * localStorage, and a future desktop/native storage swap doesn't touch the
 * component. */
export function useReauthProfiles() {
  return {
    profiles: listReauthProfiles(),
    forget: (email: string) => removeReauthProfile(email),
  };
}

/** Called from the sign-out path (8h ceiling / explicit sign-out) — the
 * device credential is cleared at the same moment (spec §4.2: "the same
 * handler is the natural place to also clear any Keychain-held re-auth
 * credentials"), so the cached profile pointing at it must not survive
 * either. */
export async function clearReauthCredentialOnSignOut(): Promise<void> {
  clearAllReauthProfiles();
  if (!reauthCredential.available) return;
  try {
    await reauthCredential.clear();
  } catch (err) {
    captureException(err instanceof Error ? err : new Error(String(err)), {
      operation: "clearReauthCredentialOnSignOut",
      source: "useAuth",
    });
  }
}

// ─── Logout ───────────────────────────────────────────────────────────────────

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Logging out of the web app must NOT clock the user out: clock-out is a
      // non-terminal break punch (sets clock_out_time, keeps the shift
      // in_progress), so auto-punching here paused the shift — re-login then
      // showed "Clock in" again and the signed-out gap was billed as an unpaid
      // break. The shift stays open; the auto-close sweep finalizes a forgotten
      // clock-out at scheduled_end + grace.
      //
      // US-176: clear the device's re-auth credential + cached profile in
      // the same breath — an explicit sign-out is a cold boundary, so the
      // next sign-in on this device must not offer a stale PIN-only path.
      await Promise.allSettled([
        logoutAndSignOut(),
        clearReauthCredentialOnSignOut(),
      ]);
    },
    // onSettled (not onSuccess): the cache-clear + full-reload redirect must run
    // even if logout rejects, otherwise a failed sign-out strands the user on the
    // current page with a live session — the stale state behind SCRUM-140. The
    // window.location reload also drops Amplify's in-memory session singleton.
    onSettled: () => {
      queryClient.clear();
      window.location.href = "/login";
    },
  });
}

// ─── Signup ───────────────────────────────────────────────────────────────────

export function useSignup() {
  return useMutation({
    mutationFn: async (data: SignupData) => {
      return signUp({
        username: data.email,
        password: data.password,
        options: {
          userAttributes: {
            email: data.email,
            ...(data.fullName ? { name: data.fullName } : {}),
          },
        },
      });
    },
  });
}

// ─── Password change ──────────────────────────────────────────────────────────

export function useUpdatePassword() {
  return useMutation({
    mutationFn: async ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => {
      await updatePassword({ oldPassword: currentPassword, newPassword });
    },
    // US-081: ProfileSettingsPage owns failure UX as an inline error alert
    // (call-site onError → setError); a global toast on top would double up.
    meta: { suppressErrorNotification: true },
  });
}

// ─── Voluntary TOTP self-enrollment ─────────────────────────────────────────
//
// US-176 Phase 2 follow-up — requireMfaProvenance (gating /me/pin and
// /me/reauth-credential) only passes a full sign-in by an account with an MFA
// factor enrolled (backend lib/mfaProvenance.ts — a live Cognito lookup; the
// ID token itself carries no `cognito:amr`). Pools with
// mfa_configuration=OPTIONAL (preprod/dev) never force enrollment, and 0/17
// preprod accounts had it on 2026-09-18, so fast-reauth setup was blocked with
// nothing the user could act on. There was no self-service enrollment path
// before this; the only one was the FORCED CONTINUE_SIGN_IN_WITH_TOTP_SETUP
// challenge, which Cognito only issues when the pool already mandates MFA.
//
// Two-step, matching Cognito's client API: setUpTOTP() returns a setup URI +
// secret; verifyTOTPSetup(code) proves possession before Cognito accepts the
// method; updateMFAPreference then marks it PREFERRED so Cognito challenges
// for it on every sign-in. The user still signs out and back in afterwards:
// PIN setup is only offered right after a sign-in completes.
//
// Account-level enrollment state (not anything about the current session).
export function useMfaPreference() {
  return useQuery({
    queryKey: ["mfa-preference"],
    queryFn: () => fetchMFAPreference(),
    staleTime: 60 * 1000,
    meta: { suppressErrorNotification: true },
  });
}

export function useSetupTotp() {
  const queryClient = useQueryClient();
  const [totpSetupUri, setTotpSetupUri] = useState<string | null>(null);
  const [enrolledThisSession, setEnrolledThisSession] = useState(false);

  const beginMutation = useMutation({
    mutationFn: async (email: string) => {
      const { getSetupUri } = await setUpTOTP();
      return getSetupUri("LivingSyncs", email).toString();
    },
    onSuccess: (uri) => setTotpSetupUri(uri),
    meta: { suppressErrorNotification: true },
  });

  const verifyMutation = useMutation({
    mutationFn: async (code: string) => {
      await verifyTOTPSetup({ code });
      await updateMFAPreference({ totp: "PREFERRED" });
    },
    onSuccess: () => {
      setTotpSetupUri(null);
      setEnrolledThisSession(true);
      queryClient.invalidateQueries({ queryKey: ["mfa-preference"] });
    },
    meta: { suppressErrorNotification: true },
  });

  return {
    // Review fix (cycle 2) — survives dismissEnrollment(). Without it,
    // clicking "Got it" before the invalidated mfa-preference refetch lands
    // dropped the card all the way back to "Set up two-factor
    // authentication", as if enrollment never happened.
    enrolledThisSession,
    begin: beginMutation.mutate,
    isBeginPending: beginMutation.isPending,
    beginError: beginMutation.error,
    totpSetupUri,
    verify: verifyMutation.mutate,
    isVerifyPending: verifyMutation.isPending,
    verifyError: verifyMutation.error,
    isEnrolled: verifyMutation.isSuccess,
    // Review fix (cycle 1) — `isEnrolled` (verifyMutation.isSuccess) is a
    // react-query mutation flag: it stays true until the mutation is reset,
    // not until the user has acknowledged it. Without an explicit dismiss,
    // the "sign out and back in" banner would persist on every future visit
    // to this page for the rest of the session, long after it stopped being
    // relevant. The caller renders a "Got it" action wired to this.
    dismissEnrollment: () => verifyMutation.reset(),
    reset: () => setTotpSetupUri(null),
  };
}

// ─── Role assumption ──────────────────────────────────────────────────────────

export function useAllowedRoles() {
  return useQuery<{
    allowedRoles: string[];
    currentRole: string | null;
    actualRoles: string[];
    isAssuming: boolean;
  }>({
    queryKey: ["allowed-roles"],
    queryFn: async () => {
      const { data } = await api.get("/auth/assume-role");
      return data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAssumeRole() {
  const queryClient = useQueryClient();
  const { setAssumedRole } = useAssumedRole();

  return useMutation({
    // US-081: error fallback centralized here; success toast is contextual
    // (role name) and lives in RoleSwitcher.
    meta: { errorFallback: "Failed to assume role" },
    mutationFn: async (role: string) => {
      const { data } = await api.post("/auth/assume-role", { role });
      return data.data;
    },
    onSuccess: (data) => {
      // Set the role FIRST so assumedRoleRef is updated synchronously and the
      // refetch below carries the X-Assumed-Role header.
      setAssumedRole(data.assumedRole); // in-memory context, not localStorage
      // Every permission-scoped list (My Tasks, dashboards, resident boards,
      // incidents, cases, …) is role-dependent — invalidate ALL queries so the
      // whole app reflects the assumed role, not just the gated UI chrome.
      queryClient.invalidateQueries();
    },
  });
}

export function useClearAssumedRole() {
  const queryClient = useQueryClient();
  const { setAssumedRole } = useAssumedRole();

  return useMutation({
    // US-081: error fallback centralized here.
    meta: { errorFallback: "Failed to clear role" },
    mutationFn: async () => {
      const { data } = await api.delete("/auth/assume-role");
      return data.data;
    },
    onSuccess: () => {
      // Clear the role FIRST so assumedRoleRef is null and the refetch drops the
      // X-Assumed-Role header (back to the user's real permissions).
      setAssumedRole(null); // in-memory context, not localStorage
      // Mirror useAssumeRole: every role-scoped list must refetch.
      queryClient.invalidateQueries();
    },
  });
}

// ─── Auditor helpers ──────────────────────────────────────────────────────────

export function useIsAuditorGroupAdmin(): boolean {
  const { data: context } = useAuth();
  return context?.isAuditorGroupAdmin ?? false;
}

export function useCanAccessAuditorGroup(auditorGroupId: string): boolean {
  const { data: context } = useAuth();
  if (!context) return false;
  if (context.isPlatformAdmin || context.isPlatformManager) return true;
  return context.auditorGroupIds.includes(auditorGroupId);
}

export function useIsExternalAuditor(): boolean {
  const { data: context } = useAuth();
  if (!context) return false;
  return context.auditorGroupIds.length > 0 && !context.isAuditorGroupAdmin;
}

export function useIsAuditor(): boolean {
  const { data: context } = useAuth();
  if (!context) return false;
  return context.isAuditorGroupAdmin || context.auditorGroupIds.length > 0;
}

// ─── Self-service profile update ─────────────────────────────────────────────

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { errorFallback: "Failed to update profile" },
    mutationFn: (data: {
      first_name?: string;
      last_name?: string;
      position?: string;
    }) => api.patch("/auth/me", data).then((r) => r.data.data),
    onSuccess: () => {
      // Refresh auth context so the header name updates immediately
      queryClient.invalidateQueries({ queryKey: ["auth-context"] });
    },
  });
}

/**
 * F-130 — a locked-out employee can recover their own account.
 *
 * There was NO recovery path of any kind: no forgot-password on the sign-in
 * page, and no admin able to trigger one. useUpdatePassword exists but takes
 * `oldPassword` and lives on the profile page, so it is useless to exactly the
 * population that cannot sign in. The locked-out user is care staff mid-shift,
 * and losing access loses the medication pass, the incident report and the
 * shift record for that shift.
 *
 * The CAPABILITY WAS ALREADY CONFIGURED and simply never surfaced: the user
 * pool declares account_recovery_setting with verified_email
 * (livingsyncs-infra/cognito/cognito.tf:54), and `inviteUser` sets
 * email_verified: true on every account it creates, so every user qualifies.
 * `allow_admin_create_user_only` disables public SIGN-UP, which is a different
 * setting and does not block recovery.
 *
 * Two steps, matching Cognito's flow: request a code, then set the new
 * password with it.
 */
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (email: string) => {
      await resetPassword({ username: email.trim().toLowerCase() });
    },
  });
}

export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: async (input: {
      email: string;
      code: string;
      newPassword: string;
    }) => {
      await confirmResetPassword({
        username: input.email.trim().toLowerCase(),
        confirmationCode: input.code.trim(),
        newPassword: input.newPassword,
      });
    },
  });
}
